import { useState } from 'react';
import { DashboardPageFormBody, DashboardPageFormShell } from '@shigo-ui/dashboard-ui';
import { Button, Skeleton, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { dashboardT } from '../../../../../lib/messages';
import {
  useStandaloneTargetingClient,
  useTargetingBuilderClient,
  useTargetingBuilderMutations,
  useTargetingBuilderQuery,
} from '../../hooks';
import {
  createTargetingBuilderFormValue,
  serializeTargetingBuilderFormValue,
  type TargetingBuilderFormValue,
} from '../../model';
import { TargetingBuilderEditorForm } from './TargetingBuilderEditorForm';

export function TargetingBuilderEditorPage({ targetingId }: { targetingId?: string | undefined }) {
  const { returnTo } = useReturnNavigation({ to: '/targeting' });
  const query = useTargetingBuilderQuery(targetingId);
  const mutations = useTargetingBuilderMutations();
  const targetingClient = useStandaloneTargetingClient();
  const builderClient = useTargetingBuilderClient();
  const editing = Boolean(targetingId);
  const [savingSequence, setSavingSequence] = useState(false);
  const saving = mutations.create.isPending || mutations.update.isPending || savingSequence;
  const loadingDetail = editing && !query.isFetchedAfterMount && !query.isError;
  const initialForm = createTargetingBuilderFormValue(query.data);

  async function save(form: TargetingBuilderFormValue) {
    const payload = serializeTargetingBuilderFormValue(form, query.data);
    setSavingSequence(true);
    try {
      if (targetingId) {
        await mutations.update.mutateAsync({ id: targetingId, payload });
        if (form.pendingBundleCsv) {
          await targetingClient.importBundlesCsv(targetingId, form.pendingBundleCsv);
        }
        if (form.pendingIpCsv) {
          await targetingClient.importIpsCsv(targetingId, form.pendingIpCsv);
        }
      } else {
        await mutations.create.mutateAsync({
          payload,
          bundlesFile: form.pendingBundleCsv,
          ipsFile: form.pendingIpCsv,
        });
      }
    } catch (error) {
      // Save errors were already shown by the mutation cache; this surfaces CSV import errors.
      showRequestError(error, dashboardT('targeting.csvImportError'));
      return;
    } finally {
      setSavingSequence(false);
    }

    toast.success(dashboardT(editing ? 'targetingBuilder.updated' : 'targetingBuilder.created'));
    try {
      // The save settled the form, so the unsaved-changes guard must not ask again.
      await returnTo({ replace: true, ignoreBlocker: true });
    } catch {
      toast.error(dashboardT('targetingBuilder.redirectError'));
    }
  }

  return (
    <DashboardPageFormShell
      title={dashboardT(editing ? 'targetingBuilder.editTitle' : 'targetingBuilder.createTitle')}
      backAction={
        <ReturnBackLink
          fallback={{ to: '/targeting' }}
          label={dashboardT('targetingBuilder.back')}
        />
      }
    >
      {loadingDetail ? (
        <div
          role="status"
          aria-label={dashboardT('targetingBuilder.loading')}
          className="grid gap-4 p-6"
        >
          <Skeleton className="h-10 w-72 max-w-full" />
          <Skeleton className="h-72" />
        </div>
      ) : null}
      {editing && query.isError ? (
        <DashboardPageFormBody title={dashboardT('targetingBuilder.loadDetailsError')}>
          <Button type="button" variant="outline" onClick={() => void query.refetch()}>
            {dashboardT('targeting.retry')}
          </Button>
        </DashboardPageFormBody>
      ) : null}
      {!editing || (!loadingDetail && !query.isError) ? (
        <TargetingBuilderEditorForm
          key={targetingId ?? 'new'}
          initialForm={initialForm}
          targetingId={targetingId}
          saving={saving}
          targetingClient={targetingClient}
          builderClient={builderClient}
          onSubmit={(form) => {
            void save(form);
          }}
        />
      ) : null}
    </DashboardPageFormShell>
  );
}
